import { db, getSqlocal } from "./db";
import { eq, and, like, or } from "drizzle-orm";
import * as s from "../../shared/src/schema";
import type {
  EventEnvelope,
  TypedEvent,
  EventTypesFor,
  EntityPrefix,
} from "../../shared/src/events";
import { optBool, str } from "../../shared/src/projectionHelpers";
import { type UserRole, HOSPITAL_SCOPED_ROLES, UNIT_SCOPED_ROLES, REGION_SCOPED_ROLES } from "../../shared/src/enums";

// Shorthand for payload access in rebuild functions
type P = Record<string, unknown>;

// ─── Re-derive-from-events projection ────────────────────────────────
// When an event arrives we re-query ALL events for the affected entity
// and fold them in clientTimestamp ASC order so the last write wins (LWW).

/** Cast database row to typed event for a specific entity prefix */
function asTypedEvent<Prefix extends EntityPrefix>(row: {
  type: string;
  payload: unknown;
  clientTimestamp: number;
}): TypedEvent<EventTypesFor<Prefix>> {
  return row as unknown as TypedEvent<EventTypesFor<Prefix>>;
}

async function getEntityEvents(entityId: string, prefix: string) {
  return db
    .select()
    .from(s.events)
    .where(
      and(eq(s.events.entityId, entityId), like(s.events.type, prefix + ".%")),
    )
    .orderBy(s.events.clientTimestamp)
    .all();
}

// ─── Helpers ─────────────────────────────────────────────────────────
function maxTs(rows: { clientTimestamp: number }[]): number {
  let m = 0;
  for (const r of rows) if (r.clientTimestamp > m) m = r.clientTimestamp;
  return m;
}

// ─── Simple entity rebuilders (no create/delete lifecycle) ──────────

async function rebuildRegion(id: string) {
  const rows = await getEntityEvents(id, "region");
  if (!rows.length) return;
  if (rows.some((r) => r.type === "region.deleted")) {
    await db.delete(s.regions).where(eq(s.regions.id, id));
    return;
  }
  let name: string | null = null;
  let archived = 0;
  for (const r of rows) {
    const e = asTypedEvent<"region">(r);
    switch (e.type) {
      case "region.create":
        break;
      case "region.setName":
        name = e.payload.name;
        break;
      case "region.archive":
        archived = 1;
        break;
      case "region.unarchive":
        archived = 0;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  await db
    .insert(s.regions)
    .values({ id, name, archived, updatedAt })
    .onConflictDoUpdate({
      target: s.regions.id,
      set: { name, archived, updatedAt },
    })
    .run();
}

async function rebuildHospital(id: string) {
  const rows = await getEntityEvents(id, "hospital");
  if (!rows.length) return;
  if (rows.some((r) => r.type === "hospital.deleted")) {
    await db.delete(s.hospitals).where(eq(s.hospitals.id, id));
    return;
  }
  let name: string | null = null;
  let regionId: string | null = null;
  let mergedInto: string | null = null;
  let archived = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "hospital.create":
        break;
      case "hospital.setName":
        name = p.name as string;
        break;
      case "hospital.setRegion":
        regionId = p.regionUuid as string;
        break;
      case "hospital.archive":
        archived = 1;
        break;
      case "hospital.unarchive":
        archived = 0;
        break;
      case "hospital.merge":
        mergedInto = p.targetUuid as string;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  await db
    .insert(s.hospitals)
    .values({ id, name, regionId, mergedInto, archived, updatedAt })
    .onConflictDoUpdate({
      target: s.hospitals.id,
      set: { name, regionId, mergedInto, archived, updatedAt },
    })
    .run();

  if (mergedInto) {
    const affectedCases = await db
      .select({ id: s.cases.id })
      .from(s.cases)
      .where(or(eq(s.cases.hospitalId, id), eq(s.cases.dischargeHospitalId, id), eq(s.cases.r2HospitalId, id)));
    for (const c of affectedCases) await rebuildCase(c.id);
  }
}

async function rebuildUnit(id: string) {
  const rows = await getEntityEvents(id, "unit");
  if (!rows.length) return;
  if (rows.some((r) => r.type === "unit.deleted")) {
    await db.delete(s.units).where(eq(s.units.id, id));
    return;
  }
  let name: string | null = null;
  let mergedInto: string | null = null;
  let archived = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "unit.create":
        break;
      case "unit.setName":
        name = p.name as string;
        break;
      case "unit.archive":
        archived = 1;
        break;
      case "unit.unarchive":
        archived = 0;
        break;
      case "unit.merge":
        mergedInto = p.targetUuid as string;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  await db
    .insert(s.units)
    .values({ id, name, mergedInto, archived, updatedAt })
    .onConflictDoUpdate({
      target: s.units.id,
      set: { name, mergedInto, archived, updatedAt },
    })
    .run();
}

async function rebuildUser(id: string) {
  const rows = await getEntityEvents(id, "user");
  if (!rows.length) return;

  // Preserve fields from existing row that may not be present in the local
  // event log (user.% events are NOT part of the client sync scope, so the
  // only events here are ones created locally — e.g. user.setRole from the
  // admin page).  Without this, email defaults to '' and the UNIQUE
  // constraint on users.email will fail when a second user is touched.
  const [existing] = await db
    .select({
      email: s.users.email,
      name: s.users.name,
      picture: s.users.picture,
      role: s.users.role,
      firstLoginAt: s.users.firstLoginAt,
      lastLoginAt: s.users.lastLoginAt,
      dashboards: s.users.dashboards,
    })
    .from(s.users)
    .where(eq(s.users.id, id));

  let email = existing?.email ?? "";
  let role: UserRole = (existing?.role ?? "unauthorized") as UserRole;
  let inviteSentAt: number | null = null;
  let canBroadcastToOwnHospital = 0;
  let canBroadcastToOwnUnit = 0;
  let dashboards: string | null = existing?.dashboards ?? null;
  const hospitals = new Set<string>();
  const unitSet = new Set<string>();
  const regionSet = new Set<string>();
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "user.create":
        email = p.email as string;
        break;
      case "user.setEmail":
        email = p.email as string;
        break;
      case "user.setRole":
        role = p.role as UserRole;
        break;
      case "user.setDashboards":
        dashboards = p.dashboards as string;
        break;
      case "user.attachHospital":
        hospitals.add(p.hospitalUuid as string);
        break;
      case "user.detachHospital":
        hospitals.delete(p.hospitalUuid as string);
        break;
      case "user.attachUnit":
        unitSet.add(p.unitUuid as string);
        break;
      case "user.detachUnit":
        unitSet.delete(p.unitUuid as string);
        break;
      case "user.attachRegion":
        regionSet.add(p.regionUuid as string);
        break;
      case "user.detachRegion":
        regionSet.delete(p.regionUuid as string);
        break;
      case "user.setCanBroadcastToOwnHospital":
        canBroadcastToOwnHospital = p.value ? 1 : 0;
        break;
      case "user.setCanBroadcastToOwnUnit":
        canBroadcastToOwnUnit = p.value ? 1 : 0;
        break;
      case "user.sendInvite":
        inviteSentAt = r.clientTimestamp;
        break;
    }
  }
  // If the user doesn't exist locally and we never saw a user.create event
  // (user.% events aren't in the sync scope), we can't construct a valid row
  // — bail out to avoid inserting with email='' which violates UNIQUE.
  if (!existing && !email) return;

  const updatedAt = maxTs(rows);
  const name = existing?.name ?? null;
  const picture = existing?.picture ?? null;
  const firstLoginAt = existing?.firstLoginAt ?? null;
  const lastLoginAt = existing?.lastLoginAt ?? null;
  await db
    .insert(s.users)
    .values({
      id,
      email,
      name,
      picture,
      role,
      firstLoginAt,
      lastLoginAt,
      inviteSentAt,
      canBroadcastToOwnHospital,
      canBroadcastToOwnUnit,
      dashboards,
      updatedAt,
    })
    .onConflictDoUpdate({
      target: s.users.id,
      set: {
        email,
        name,
        picture,
        role,
        firstLoginAt,
        lastLoginAt,
        inviteSentAt,
        canBroadcastToOwnHospital,
        canBroadcastToOwnUnit,
        dashboards,
        updatedAt,
      },
    })
    .run();

  // Rebuild junction tables: only persist data relevant to the role
  await db.delete(s.userHospitals).where(eq(s.userHospitals.userId, id)).run();
  if (HOSPITAL_SCOPED_ROLES.includes(role)) {
    for (const hId of hospitals) {
      await db
        .insert(s.userHospitals)
        .values({ userId: id, hospitalId: hId })
        .onConflictDoNothing()
        .run();
    }
  }
  await db.delete(s.userUnits).where(eq(s.userUnits.userId, id)).run();
  if (UNIT_SCOPED_ROLES.includes(role)) {
    for (const uId of unitSet) {
      await db
        .insert(s.userUnits)
        .values({ userId: id, unitId: uId })
        .onConflictDoNothing()
        .run();
    }
  }
  await db.delete(s.userRegions).where(eq(s.userRegions.userId, id)).run();
  if (REGION_SCOPED_ROLES.includes(role)) {
    for (const rId of regionSet) {
      await db
        .insert(s.userRegions)
        .values({ userId: id, regionId: rId })
        .onConflictDoNothing()
        .run();
    }
  }
}

async function rebuildSession(id: string) {
  const rows = await getEntityEvents(id, "session");
  if (!rows.length) return;
  let userId = "";
  let destroyed = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "session.create":
        userId = p.userUuid as string;
        break;
      case "session.destroy":
        destroyed = 1;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  await db
    .insert(s.sessions)
    .values({ id, userId, destroyed, updatedAt })
    .onConflictDoUpdate({
      target: s.sessions.id,
      set: { userId, destroyed, updatedAt },
    })
    .run();
}

async function rebuildPatient(id: string) {
  const rows = await getEntityEvents(id, "patient");
  if (!rows.length) return;
  if (rows.some((r) => r.type === "patient.deleted")) {
    await db.delete(s.patients).where(eq(s.patients.id, id));
    return;
  }
  let unitId: string | null = null;
  let mergedInto: string | null = null;
  let name: string | null = null;
  let dateOfBirth: string | null = null;
  let sex: string | null = null;
  let bloodType: string | null = null;
  let rhFactor: string | null = null;
  let allergyStatus: string | null = null;
  let allergyDetails: string | null = null;
  let militaryRank: string | null = null;
  let idNumber: string | null = null;
  let patientCategory: string | null = null;
  let personalDataErased = 0;
  let archived = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "patient.create":
        unitId = p.unitUuid as string;
        break;
      case "patient.setUnit":
        unitId = p.unitUuid as string;
        break;
      case "patient.setName":
        name = p.name as string;
        break;
      case "patient.setDateOfBirth":
        dateOfBirth = p.date as string;
        break;
      case "patient.setSex":
        sex = p.sex as string;
        break;
      case "patient.setBloodType":
        bloodType = p.bloodType as string;
        break;
      case "patient.setRhFactor":
        rhFactor = p.rhFactor as string;
        break;
      case "patient.setAllergyStatus":
        allergyStatus = p.status as string;
        break;
      case "patient.setAllergyDetails":
        allergyDetails = p.details as string;
        break;
      case "patient.setMilitaryRank":
        militaryRank = p.rank as string;
        break;
      case "patient.setIdNumber":
        idNumber = p.idNumber as string;
        break;
      case "patient.setCategory":
        patientCategory = p.category as string;
        break;
      case "patient.erasePersonalData":
        name = null;
        dateOfBirth = null;
        sex = null;
        bloodType = null;
        rhFactor = null;
        allergyStatus = null;
        allergyDetails = null;
        militaryRank = null;
        idNumber = null;
        patientCategory = null;
        personalDataErased = 1;
        break;
      case "patient.merge":
        mergedInto = p.targetUuid as string;
        break;
      case "patient.archive":
        archived = 1;
        break;
      case "patient.unarchive":
        archived = 0;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  const vals = {
    id,
    unitId,
    mergedInto,
    name,
    dateOfBirth,
    sex,
    bloodType,
    rhFactor,
    allergyStatus,
    allergyDetails,
    militaryRank,
    idNumber,
    patientCategory,
    personalDataErased,
    archived,
    updatedAt,
  };
  await db
    .insert(s.patients)
    .values(vals)
    .onConflictDoUpdate({
      target: s.patients.id,
      set: {
        unitId,
        mergedInto,
        name,
        dateOfBirth,
        sex,
        bloodType,
        rhFactor,
        allergyStatus,
        allergyDetails,
        militaryRank,
        idNumber,
        patientCategory,
        personalDataErased,
        archived,
        updatedAt,
      },
    })
    .run();

  // If merged, re-project all cases that referenced this patient
  if (mergedInto) {
    const affectedCases = await db
      .select({ id: s.cases.id })
      .from(s.cases)
      .where(eq(s.cases.patientId, id));
    for (const c of affectedCases) {
      await rebuildCase(c.id);
    }
  }
}

async function rebuildCase(id: string) {
  const rows = await getEntityEvents(id, "case");
  if (!rows.length) return;
  if (rows.some((r) => r.type === "case.deleted")) {
    await db.delete(s.cases).where(eq(s.cases.id, id));
    return;
  }
  let patientId = "";
  let unitId: string | null = null;
  let number: string | null = null;
  let hospitalId: string | null = null;
  let eventTime: number | null = null;
  let eventLocation: string | null = null;
  let arrivalTime: number | null = null;
  let triageCategory: string | null = null;
  let role: string | null = null;
  let medicalFacility: string | null = null;
  let transportType: string[] | null = null;
  let traumaTypes: unknown = null;
  let traumaPenetrating: number | null = null;
  let traumaNonPenetrating: number | null = null;
  let traumaLocations: unknown = null;
  let conditionTypes: unknown = null;
  let cbrn: unknown = null;
  let priorCare: unknown = null;
  let priorCareTime: number | null = null;
  let tetanusProphylaxis: string | null = null;
  let deathTime: number | null = null;
  let deathCircumstances: string | null = null;
  let dischargeTime: number | null = null;
  let dischargeDestination: string | null = null;
  let dischargeHospitalId: string | null = null;
  let dischargeUnitId: string | null = null;
  let evacuationCategory: string | null = null;
  let dischargeTransportType: string[] | null = null;
  let dischargePosition: string | null = null;
  let escortDoctor: number | null = null;
  let escortNurse: number | null = null;
  let dischargeRole: string | null = null;
  let dischargeVehicleType: string | null = null;
  let dischargeEquipment: unknown = null;
  let diagnosis: string | null = null;
  let notes: string | null = null;
  let medicName: string | null = null;
  let medicFacility: string | null = null;
  let medicRole: string | null = null;
  let r1Care: unknown = null;
  let r1Kit1: string | null = null;
  let r1Kit2: string | null = null;
  let r2ArrivalTime: number | null = null;
  let r2TriageCategory: string | null = null;
  let r2HospitalId: string | null = null;
  let r2Care: unknown = null;
  let r2DeathTime: number | null = null;
  let r2DeathCircumstances: string | null = null;
  let r2DischargeTime: number | null = null;
  let r2DischargeDestination: string | null = null;
  let r2EvacuationCategory: string | null = null;
  let r2DischargeTransportType: string[] | null = null;
  let r2DischargePosition: string | null = null;
  let r2EscortDoctor: number | null = null;
  let r2EscortNurse: number | null = null;
  let r2DischargeRole: string | null = null;
  let r2DischargeVehicleType: string | null = null;
  let r2DischargeEquipment: unknown = null;
  let r1PriorCareStatus: string | null = null;
  let r2Diagnosis: string | null = null;
  let r2Notes: string | null = null;
  let r2MedicName: string | null = null;
  let r2MedicFacility: string | null = null;
  let r2TetanusProphylaxis: string | null = null;
  let r2Kit1: string | null = null;
  let r2Kit2: string | null = null;
  let r2MedicRole: string | null = null;
  let signatureSha256: string | null = null;
  let r2SignatureSha256: string | null = null;
  let createdBy: string | null = null;
  let archived = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "case.create":
        createdBy = r.userId;
        break;
      case "case.setPatient": {
        patientId = p.patientUuid as string;
        // Follow merge chain: if this patient was merged, resolve to the final target
        let resolved = patientId;
        for (let i = 0; i < 10; i++) {
          // guard against cycles
          const [mp] = await db
            .select({ mergedInto: s.patients.mergedInto })
            .from(s.patients)
            .where(eq(s.patients.id, resolved));
          if (mp?.mergedInto) resolved = mp.mergedInto;
          else break;
        }
        patientId = resolved;
        // Denormalize: snapshot patient's unit
        const [pt] = await db
          .select({ unitId: s.patients.unitId })
          .from(s.patients)
          .where(eq(s.patients.id, patientId));
        if (pt?.unitId) unitId = pt.unitId;
        break;
      }
      case "case.setNumber":
        number = p.number as string;
        break;
      case "case.setHospital":
        hospitalId = p.hospitalUuid as string;
        break;
      case "case.setEventTime":
        eventTime = p.timestamp as number;
        break;
      case "case.setEventLocation":
        eventLocation = p.location as string;
        break;
      case "case.setArrivalTime":
        arrivalTime = p.timestamp as number;
        break;
      case "case.setTriageCategory":
        triageCategory = p.triageCategory as string;
        break;
      case "case.setRole":
        role = str(p.role);
        break;
      case "case.setMedicalFacility":
        medicalFacility = p.facility as string;
        break;
      case "case.setTransportType": {
        const v = p.transportType as string;
        transportType = v ? [v] : [];
        break;
      }
      case "case.setTransportTypeItem": {
        const toggle = (arr: string[] | null) => {
          const a = arr || [];
          return p.value
            ? a.includes(p.key as string) ? a : [...a, p.key as string]
            : a.filter((x) => x !== p.key);
        };
        if (p.field === "arrival") transportType = toggle(transportType);
        else if (p.field === "discharge") dischargeTransportType = toggle(dischargeTransportType);
        else if (p.field === "r2Discharge") r2DischargeTransportType = toggle(r2DischargeTransportType);
        break;
      }
      case "case.setTraumaTypeItem": {
        const a = (traumaTypes as string[]) || [];
        traumaTypes = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x) => x !== p.key);
        break;
      }
      case "case.setTraumaPenetrating":
        traumaPenetrating = optBool(p.penetrating);
        break;
      case "case.setTraumaNonPenetrating":
        traumaNonPenetrating = optBool(p.value);
        break;
      case "case.setTraumaBurnItem": {
        const tl = (traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        const a = tl.burns || [];
        traumaLocations = {
          ...tl,
          burns: p.value
            ? a.includes(p.zone as string)
              ? a
              : [...a, p.zone as string]
            : a.filter((z: string) => z !== p.zone),
        };
        break;
      }
      case "case.setTraumaZoneItem": {
        const tl = (traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        const a = tl.traumas || [];
        traumaLocations = {
          ...tl,
          traumas: p.value
            ? a.includes(p.zone as string)
              ? a
              : [...a, p.zone as string]
            : a.filter((z: string) => z !== p.zone),
        };
        break;
      }
      case "case.addInjuryMark": {
        const tl = (traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        traumaLocations = {
          ...tl,
          injuries: [
            ...(tl.injuries || []),
            { id: p.markId as string, x: p.x as number, y: p.y as number },
          ],
        };
        break;
      }
      case "case.removeInjuryMark": {
        const tl = (traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        traumaLocations = {
          ...tl,
          injuries: (tl.injuries || []).filter((m: any) => m.id !== p.markId),
        };
        break;
      }
      case "case.setConditionTypeItem": {
        const a = (conditionTypes as string[]) || [];
        conditionTypes = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x) => x !== p.key);
        break;
      }
      case "case.setCbrnItem": {
        const a = (cbrn as string[]) || [];
        cbrn = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x) => x !== p.key);
        break;
      }
      case "case.setPriorCareItem":
        priorCare = {
          ...((priorCare as Record<string, unknown>) || {}),
          [p.key as string]: p.value,
        };
        break;
      case "case.setPriorCareTime":
        priorCareTime = p.timestamp as number;
        break;
      case "case.setTetanusProphylaxis":
        tetanusProphylaxis = p.prophylaxis as string;
        break;
      case "case.setDeathTime":
        deathTime = p.timestamp as number;
        break;
      case "case.setDeathCircumstances":
        deathCircumstances = p.circumstances as string;
        break;
      case "case.setDischargeTime":
        dischargeTime = p.timestamp as number;
        break;
      case "case.setDischargeDestination":
        dischargeDestination = p.destination as string;
        break;
      case "case.setDischargeHospital":
        dischargeHospitalId = p.hospitalId as string | null;
        break;
      case "case.setDischargeUnit":
        dischargeUnitId = p.unitId as string | null;
        break;
      case "case.setEvacuationCategory":
        evacuationCategory = p.evacuationCategory as string;
        break;
      case "case.setDischargeTransportType": {
        const v = p.transportType as string;
        dischargeTransportType = v ? [v] : [];
        break;
      }
      case "case.setDischargePosition":
        dischargePosition = p.position as string;
        break;
      case "case.setEscortDoctor":
        escortDoctor = optBool(p.escortDoctor);
        break;
      case "case.setEscortNurse":
        escortNurse = optBool(p.escortNurse);
        break;
      case "case.setDischargeRole":
        dischargeRole = str(p.role);
        break;
      case "case.setDischargeVehicleType":
        dischargeVehicleType = str(p.vehicleType);
        break;
      case "case.setDischargeEquipmentItem":
        dischargeEquipment = {
          ...((dischargeEquipment as Record<string, boolean>) || {}),
          [p.key as string]: p.value as boolean,
        };
        break;
      case "case.setDiagnosis":
        diagnosis = p.diagnosis as string;
        break;
      case "case.setNotes":
        notes = p.notes as string;
        break;
      case "case.setMedicName":
        medicName = p.name as string;
        break;
      case "case.setMedicFacility":
        medicFacility = p.facility as string;
        break;
      case "case.setMedicRole":
        medicRole = p.role as string;
        break;
      case "case.setR1CareItem":
        r1Care = {
          ...((r1Care as Record<string, boolean | string>) || {}),
          [p.key as string]: p.value,
        };
        break;
      case "case.setR1Kit1":
        r1Kit1 = p.kit as string;
        break;
      case "case.setR1Kit2":
        r1Kit2 = p.kit as string;
        break;
      case "case.setR2ArrivalTime":
        r2ArrivalTime = p.timestamp as number;
        break;
      case "case.setR2TriageCategory":
        r2TriageCategory = p.triageCategory as string;
        break;
      case "case.setR2Hospital":
        r2HospitalId = p.hospitalUuid as string;
        break;
      case "case.setR2CareItem":
        r2Care = {
          ...((r2Care as Record<string, boolean | string>) || {}),
          [p.key as string]: p.value,
        };
        break;
      case "case.setR2DeathTime":
        r2DeathTime = p.timestamp as number;
        break;
      case "case.setR2DeathCircumstances":
        r2DeathCircumstances = p.circumstances as string;
        break;
      case "case.setR2DischargeTime":
        r2DischargeTime = p.timestamp as number;
        break;
      case "case.setR2DischargeDestination":
        r2DischargeDestination = p.destination as string;
        break;
      case "case.setR2EvacuationCategory":
        r2EvacuationCategory = p.evacuationCategory as string;
        break;
      case "case.setR2DischargeTransportType": {
        const v = p.transportType as string;
        r2DischargeTransportType = v ? [v] : [];
        break;
      }
      case "case.setR2DischargePosition":
        r2DischargePosition = p.position as string;
        break;
      case "case.setR2EscortDoctor":
        r2EscortDoctor = optBool(p.escortDoctor);
        break;
      case "case.setR2EscortNurse":
        r2EscortNurse = optBool(p.escortNurse);
        break;
      case "case.setR2DischargeRole":
        r2DischargeRole = str(p.role);
        break;
      case "case.setR2DischargeVehicleType":
        r2DischargeVehicleType = str(p.vehicleType);
        break;
      case "case.setR2DischargeEquipmentItem":
        r2DischargeEquipment = {
          ...((r2DischargeEquipment as Record<string, boolean>) || {}),
          [p.key as string]: p.value as boolean,
        };
        break;
      case "case.setR1PriorCareStatus":
        r1PriorCareStatus = str(p.status);
        break;
      case "case.setR2Diagnosis":
        r2Diagnosis = p.diagnosis as string;
        break;
      case "case.setR2Notes":
        r2Notes = p.notes as string;
        break;
      case "case.setR2MedicName":
        r2MedicName = p.name as string;
        break;
      case "case.setR2MedicFacility":
        r2MedicFacility = p.facility as string;
        break;
      case "case.setR2MedicRole":
        r2MedicRole = p.role as string;
        break;
      case "case.setR2TetanusProphylaxis":
        r2TetanusProphylaxis = p.prophylaxis as string;
        break;
      case "case.setR2Kit1":
        r2Kit1 = p.kit as string;
        break;
      case "case.setR2Kit2":
        r2Kit2 = p.kit as string;
        break;
      case "case.setSignatureData":
        signatureSha256 = p.sha256 as string;
        break;
      case "case.setR2SignatureData":
        r2SignatureSha256 = p.sha256 as string;
        break;
      case "case.archive":
        archived = 1;
        break;
      case "case.unarchive":
        archived = 0;
        break;
    }
  }

  // Follow hospital merge chains
  const resolveHospital = async (hId: string): Promise<string> => {
    let resolved = hId;
    for (let i = 0; i < 10; i++) {
      const [row] = await db.select({ mergedInto: s.hospitals.mergedInto }).from(s.hospitals).where(eq(s.hospitals.id, resolved));
      if (row?.mergedInto) resolved = row.mergedInto; else break;
    }
    return resolved;
  };
  if (hospitalId) hospitalId = await resolveHospital(hospitalId);
  if (dischargeHospitalId) dischargeHospitalId = await resolveHospital(dischargeHospitalId);
  if (r2HospitalId) r2HospitalId = await resolveHospital(r2HospitalId);

  const updatedAt = maxTs(rows);
  const set = {
    patientId,
    unitId,
    number,
    hospitalId,
    eventTime,
    eventLocation,
    arrivalTime,
    triageCategory,
    role,
    medicalFacility,
    transportType,
    traumaTypes: traumaTypes as any,
    traumaPenetrating,
    traumaNonPenetrating,
    traumaLocations: traumaLocations as any,
    conditionTypes: conditionTypes as any,
    cbrn: cbrn as any,
    priorCare: priorCare as any,
    priorCareTime,
    tetanusProphylaxis,
    deathTime,
    deathCircumstances,
    dischargeTime,
    dischargeDestination,
    dischargeHospitalId,
    dischargeUnitId,
    evacuationCategory,
    dischargeTransportType,
    dischargePosition,
    escortDoctor,
    escortNurse,
    dischargeRole,
    dischargeVehicleType,
    dischargeEquipment: dischargeEquipment as any,
    diagnosis,
    notes,
    medicName,
    medicFacility,
    medicRole,
    r1Care: r1Care as any,
    r1Kit1,
    r1Kit2,
    r2ArrivalTime,
    r2TriageCategory,
    r2HospitalId,
    r2Care: r2Care as any,
    r2DeathTime,
    r2DeathCircumstances,
    r2DischargeTime,
    r2DischargeDestination,
    r2EvacuationCategory,
    r2DischargeTransportType,
    r2DischargePosition,
    r2EscortDoctor,
    r2EscortNurse,
    r2DischargeRole,
    r2DischargeVehicleType,
    r2DischargeEquipment: r2DischargeEquipment as any,
    r1PriorCareStatus,
    r2TetanusProphylaxis,
    r2Kit1,
    r2Kit2,
    r2Diagnosis,
    r2Notes,
    r2MedicName,
    r2MedicFacility,
    r2MedicRole,
    signatureSha256,
    r2SignatureSha256,
    createdBy,
    archived,
    updatedAt,
  };
  await db
    .insert(s.cases)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.cases.id, set })
    .run();
}

// ─── Deletable time-series rebuilders ────────────────────────────────
// These entities have create/delete lifecycle. If the last delete has a
// later clientTimestamp than the last create, the row is removed.

async function rebuildVitals(id: string) {
  const rows = await getEntityEvents(id, "vitals");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let respiratoryRate: number | null = null;
  let spO2: number | null = null;
  let pulse: number | null = null;
  let systolic: number | null = null;
  let diastolic: number | null = null;
  let gcsEye: number | null = null;
  let gcsVerbal: number | null = null;
  let gcsMotor: number | null = null;
  let painScale: number | null = null;
  let temperature: string | null = null;
  let diuresis: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "vitals.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "vitals.delete":
        deleted = true;
        break;
      case "vitals.setTime":
        time = p.timestamp as number;
        break;
      case "vitals.setRespiratoryRate":
        respiratoryRate = p.value as number;
        break;
      case "vitals.setSpO2":
        spO2 = p.value as number;
        break;
      case "vitals.setPulse":
        pulse = p.value as number;
        break;
      case "vitals.setSystolic":
        systolic = p.value as number;
        break;
      case "vitals.setDiastolic":
        diastolic = p.value as number;
        break;
      case "vitals.setGcsEye":
        gcsEye = p.value as number;
        break;
      case "vitals.setGcsVerbal":
        gcsVerbal = p.value as number;
        break;
      case "vitals.setGcsMotor":
        gcsMotor = p.value as number;
        break;
      case "vitals.setPainScale":
        painScale = p.value as number;
        break;
      case "vitals.setTemperature":
        temperature = str(p.value);
        break;
      case "vitals.setDiuresis":
        diuresis = p.value as number;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseVitals).where(eq(s.caseVitals.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    time,
    respiratoryRate,
    spO2,
    pulse,
    systolic,
    diastolic,
    gcsEye,
    gcsVerbal,
    gcsMotor,
    painScale,
    temperature,
    diuresis,
    updatedAt,
  };
  await db
    .insert(s.caseVitals)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseVitals.id, set })
    .run();
}

async function rebuildVentilation(id: string) {
  const rows = await getEntityEvents(id, "ventilation");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let mode: string | null = null;
  let rate: number | null = null;
  let tidalVolume: number | null = null;
  let fiO2: number | null = null;
  let peep: number | null = null;
  let pip: number | null = null;
  let etCO2: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "ventilation.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "ventilation.delete":
        deleted = true;
        break;
      case "ventilation.setTime":
        time = p.timestamp as number;
        break;
      case "ventilation.setMode":
        mode = p.mode as string;
        break;
      case "ventilation.setRate":
        rate = p.value as number;
        break;
      case "ventilation.setTidalVolume":
        tidalVolume = p.value as number;
        break;
      case "ventilation.setFiO2":
        fiO2 = p.value as number;
        break;
      case "ventilation.setPeep":
        peep = p.value as number;
        break;
      case "ventilation.setPip":
        pip = p.value as number;
        break;
      case "ventilation.setEtCO2":
        etCO2 = p.value as number;
        break;
    }
  }
  if (deleted) {
    await db
      .delete(s.caseVentilation)
      .where(eq(s.caseVentilation.id, id))
      .run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    time,
    mode,
    rate,
    tidalVolume,
    fiO2,
    peep,
    pip,
    etCO2,
    updatedAt,
  };
  await db
    .insert(s.caseVentilation)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseVentilation.id, set })
    .run();
}

async function rebuildDiagnostics(id: string) {
  const rows = await getEntityEvents(id, "diagnostics");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let eFast: string | null = null;
  let xray: string | null = null;
  let ct: string | null = null;
  let ecg: string | null = null;
  let ultrasound: string | null = null;
  let otoscopyLeft: string | null = null;
  let otoscopyRight: string | null = null;
  let pupilsLeft: string | null = null;
  let pupilsRight: string | null = null;
  let other: string | null = null;
  let notes: string | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "diagnostics.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "diagnostics.delete":
        deleted = true;
        break;
      case "diagnostics.setTime":
        time = p.timestamp as number;
        break;
      case "diagnostics.setEFast":
        eFast = p.value as string;
        break;
      case "diagnostics.setXray":
        xray = p.value as string;
        break;
      case "diagnostics.setCt":
        ct = p.value as string;
        break;
      case "diagnostics.setEcg":
        ecg = p.value as string;
        break;
      case "diagnostics.setUltrasound":
        ultrasound = p.value as string;
        break;
      case "diagnostics.setOtoscopyLeft":
        otoscopyLeft = p.value as string;
        break;
      case "diagnostics.setOtoscopyRight":
        otoscopyRight = p.value as string;
        break;
      case "diagnostics.setPupilsLeft":
        pupilsLeft = p.value as string;
        break;
      case "diagnostics.setPupilsRight":
        pupilsRight = p.value as string;
        break;
      case "diagnostics.setOther":
        other = p.value as string;
        break;
      case "diagnostics.setNotes":
        notes = p.value as string;
        break;
    }
  }
  if (deleted) {
    await db
      .delete(s.caseDiagnostics)
      .where(eq(s.caseDiagnostics.id, id))
      .run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    time,
    eFast,
    xray,
    ct,
    ecg,
    ultrasound,
    otoscopyLeft,
    otoscopyRight,
    pupilsLeft,
    pupilsRight,
    other,
    notes,
    updatedAt,
  };
  await db
    .insert(s.caseDiagnostics)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseDiagnostics.id, set })
    .run();
}

async function rebuildLabValues(id: string) {
  const rows = await getEntityEvents(id, "labValues");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let hg: string | null = null;
  let hct: string | null = null;
  let rbc: string | null = null;
  let wbc: string | null = null;
  let platelets: string | null = null;
  let na: string | null = null;
  let k: string | null = null;
  let ca: string | null = null;
  let cl: string | null = null;
  let lactate: string | null = null;
  let urea: string | null = null;
  let creatinine: string | null = null;
  let glucose: string | null = null;
  let ph: string | null = null;
  let pCO2: number | null = null;
  let pO2: number | null = null;
  let hCO3: string | null = null;
  let notes: string | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "labValues.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "labValues.delete":
        deleted = true;
        break;
      case "labValues.setTime":
        time = p.timestamp as number;
        break;
      case "labValues.setHg":
        hg = str(p.value);
        break;
      case "labValues.setHct":
        hct = str(p.value);
        break;
      case "labValues.setRbc":
        rbc = str(p.value);
        break;
      case "labValues.setWbc":
        wbc = str(p.value);
        break;
      case "labValues.setPlatelets":
        platelets = str(p.value);
        break;
      case "labValues.setNa":
        na = str(p.value);
        break;
      case "labValues.setK":
        k = str(p.value);
        break;
      case "labValues.setCa":
        ca = str(p.value);
        break;
      case "labValues.setCl":
        cl = str(p.value);
        break;
      case "labValues.setLactate":
        lactate = str(p.value);
        break;
      case "labValues.setUrea":
        urea = str(p.value);
        break;
      case "labValues.setCreatinine":
        creatinine = str(p.value);
        break;
      case "labValues.setGlucose":
        glucose = str(p.value);
        break;
      case "labValues.setPh":
        ph = str(p.value);
        break;
      case "labValues.setPCO2":
        pCO2 = Number(p.value);
        break;
      case "labValues.setPO2":
        pO2 = Number(p.value);
        break;
      case "labValues.setHCO3":
        hCO3 = str(p.value);
        break;
      case "labValues.setNotes":
        notes = p.value as string;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseLabValues).where(eq(s.caseLabValues.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    time,
    hg,
    hct,
    rbc,
    wbc,
    platelets,
    na,
    k,
    ca,
    cl,
    lactate,
    urea,
    creatinine,
    glucose,
    ph,
    pCO2,
    pO2,
    hCO3,
    notes,
    updatedAt,
  };
  await db
    .insert(s.caseLabValues)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseLabValues.id, set })
    .run();
}

async function rebuildExam(id: string) {
  const rows = await getEntityEvents(id, "exam");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let airwayStatus: string | null = null;
  let advancedAirwayType: string | null = null;
  let respiratorySupportType: string | null = null;
  let breathingSounds: unknown = null;
  let skinColor: string | null = null;
  let skinMoisture: number | null = null;
  let skinTemperature: string | null = null;
  let skinEdema: number | null = null;
  let skinRash: number | null = null;
  let skinPink: number | null = null;
  let skinPale: number | null = null;
  let skinCyanotic: number | null = null;
  let skinDry: number | null = null;
  let skinMoist: number | null = null;
  let chestSymmetric: number | null = null;
  let chestAsymRight: number | null = null;
  let chestAsymLeft: number | null = null;
  let chestFlailLeft: number | null = null;
  let chestFlailRight: number | null = null;
  let chestPenetratingLeft: number | null = null;
  let chestPenetratingRight: number | null = null;
  let heartSoundsClear: number | null = null;
  let tracheaMidline: number | null = null;
  let tracheaDeviationRight: number | null = null;
  let tracheaDeviationLeft: number | null = null;
  let peripheralPulse: string | null = null;
  let capillaryRefillOver2s: number | null = null;
  let jugularVeinsCollapsed: number | null = null;
  let jugularVeinsDistended: number | null = null;
  let abdomenDistended: number | null = null;
  let abdomenPainful: number | null = null;
  let peritonealIrritation: number | null = null;
  let abdomenPenetratingTrauma: number | null = null;
  let pelvisStable: number | null = null;
  let limbDeformation: unknown = null;
  let neuroDeficit: unknown = null;
  let deformationAny: number | null = null;
  let neuroDeficitAny: number | null = null;
  let pupilsEqual: string | null = null;
  let pupilsLightReaction: number | null = null;
  let pupilsLightReactionLeft: number | null = null;
  let pupilsLightReactionRight: number | null = null;
  let visionLeft: string | null = null;
  let visionRight: string | null = null;
  let skullFracture: number | null = null;
  let csfLeak: number | null = null;
  let cervicalSpineInjury: number | null = null;
  let gcsTotal: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "exam.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "exam.delete":
        deleted = true;
        break;
      case "exam.setTime":
        time = p.timestamp as number;
        break;
      case "exam.setAirwayStatus":
        airwayStatus = p.airwayStatus as string;
        break;
      case "exam.setAdvancedAirwayType":
        advancedAirwayType = p.value as string;
        break;
      case "exam.setRespiratorySupportType":
        respiratorySupportType = p.value as string;
        break;
      case "exam.setBreathingSoundsItem":
        breathingSounds = {
          ...((breathingSounds as Record<string, boolean>) || {}),
          [p.key as string]: p.value as boolean,
        };
        break;
      case "exam.setSkinColor":
        skinColor = p.value as string;
        break;
      case "exam.setSkinMoisture":
        skinMoisture = optBool(p.value);
        break;
      case "exam.setSkinTemperature":
        skinTemperature = p.value as string;
        break;
      case "exam.setSkinEdema":
        skinEdema = optBool(p.value);
        break;
      case "exam.setSkinRash":
        skinRash = optBool(p.value);
        break;
      case "exam.setSkinPink":
        skinPink = optBool(p.value);
        break;
      case "exam.setSkinPale":
        skinPale = optBool(p.value);
        break;
      case "exam.setSkinCyanotic":
        skinCyanotic = optBool(p.value);
        break;
      case "exam.setSkinDry":
        skinDry = optBool(p.value);
        break;
      case "exam.setSkinMoist":
        skinMoist = optBool(p.value);
        break;
      case "exam.setChestSymmetric":
        chestSymmetric = optBool(p.value);
        break;
      case "exam.setChestAsymRight":
        chestAsymRight = optBool(p.value);
        break;
      case "exam.setChestAsymLeft":
        chestAsymLeft = optBool(p.value);
        break;
      case "exam.setChestFlailLeft":
        chestFlailLeft = optBool(p.value);
        break;
      case "exam.setChestFlailRight":
        chestFlailRight = optBool(p.value);
        break;
      case "exam.setChestPenetratingLeft":
        chestPenetratingLeft = optBool(p.value);
        break;
      case "exam.setChestPenetratingRight":
        chestPenetratingRight = optBool(p.value);
        break;
      case "exam.setHeartSoundsClear":
        heartSoundsClear = optBool(p.value);
        break;
      case "exam.setTracheaMidline":
        tracheaMidline = optBool(p.value);
        break;
      case "exam.setTracheaDeviationRight":
        tracheaDeviationRight = optBool(p.value);
        break;
      case "exam.setTracheaDeviationLeft":
        tracheaDeviationLeft = optBool(p.value);
        break;
      case "exam.setPeripheralPulse":
        peripheralPulse = p.value as string;
        break;
      case "exam.setCapillaryRefillOver2s":
        capillaryRefillOver2s = optBool(p.value);
        break;
      case "exam.setJugularVeinsCollapsed":
        jugularVeinsCollapsed = optBool(p.value);
        break;
      case "exam.setJugularVeinsDistended":
        jugularVeinsDistended = optBool(p.value);
        break;
      case "exam.setAbdomenDistended":
        abdomenDistended = optBool(p.value);
        break;
      case "exam.setAbdomenPainful":
        abdomenPainful = optBool(p.value);
        break;
      case "exam.setPeritonealIrritation":
        peritonealIrritation = optBool(p.value);
        break;
      case "exam.setAbdomenPenetratingTrauma":
        abdomenPenetratingTrauma = optBool(p.value);
        break;
      case "exam.setPelvisStable":
        pelvisStable = optBool(p.value);
        break;
      case "exam.setLimbDeformationItem":
        limbDeformation =
          p.key === "_all"
            ? {}
            : {
                ...((limbDeformation as Record<string, boolean>) || {}),
                [p.key as string]: p.value,
              };
        break;
      case "exam.setNeuroDeficitItem":
        neuroDeficit =
          p.key === "_all"
            ? {}
            : {
                ...((neuroDeficit as Record<string, boolean>) || {}),
                [p.key as string]: p.value,
              };
        break;
      case "exam.setDeformationAny":
        deformationAny = optBool(p.value);
        break;
      case "exam.setNeuroDeficitAny":
        neuroDeficitAny = optBool(p.value);
        break;
      case "exam.setPupilsEqual":
        pupilsEqual = p.value;
        break;
      case "exam.setPupilsLightReaction":
        pupilsLightReaction = optBool(p.value);
        break;
      case "exam.setPupilsLightReactionLeft":
        pupilsLightReactionLeft = optBool(p.value);
        break;
      case "exam.setPupilsLightReactionRight":
        pupilsLightReactionRight = optBool(p.value);
        break;
      case "exam.setVisionLeft":
        visionLeft = p.value as string;
        break;
      case "exam.setVisionRight":
        visionRight = p.value as string;
        break;
      case "exam.setSkullFracture":
        skullFracture = optBool(p.value);
        break;
      case "exam.setCsfLeak":
        csfLeak = optBool(p.value);
        break;
      case "exam.setCervicalSpineInjury":
        cervicalSpineInjury = optBool(p.value);
        break;
      case "exam.setGcsTotal":
        gcsTotal = p.value as number;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseExam).where(eq(s.caseExam.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    time,
    airwayStatus,
    advancedAirwayType,
    respiratorySupportType,
    breathingSounds: breathingSounds as any,
    skinColor,
    skinMoisture,
    skinTemperature,
    skinEdema,
    skinRash,
    skinPink,
    skinPale,
    skinCyanotic,
    skinDry,
    skinMoist,
    chestSymmetric,
    chestAsymRight,
    chestAsymLeft,
    chestFlailLeft,
    chestFlailRight,
    chestPenetratingLeft,
    chestPenetratingRight,
    heartSoundsClear,
    tracheaMidline,
    tracheaDeviationRight,
    tracheaDeviationLeft,
    peripheralPulse,
    capillaryRefillOver2s,
    jugularVeinsCollapsed,
    jugularVeinsDistended,
    abdomenDistended,
    abdomenPainful,
    peritonealIrritation,
    abdomenPenetratingTrauma,
    pelvisStable,
    limbDeformation: limbDeformation as any,
    neuroDeficit: neuroDeficit as any,
    deformationAny,
    neuroDeficitAny,
    pupilsEqual,
    pupilsLightReaction,
    pupilsLightReactionLeft,
    pupilsLightReactionRight,
    visionLeft,
    visionRight,
    skullFracture,
    csfLeak,
    cervicalSpineInjury,
    gcsTotal,
    updatedAt,
  };
  await db
    .insert(s.caseExam)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseExam.id, set })
    .run();
}

async function rebuildCpr(id: string) {
  const rows = await getEntityEvents(id, "cpr");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let startTime: number | null = null;
  let endTime: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "cpr.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "cpr.delete":
        deleted = true;
        break;
      case "cpr.setStartTime":
        startTime = p.timestamp as number;
        break;
      case "cpr.setEndTime":
        endTime = p.timestamp as number;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseCpr).where(eq(s.caseCpr.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = { caseId, role, startTime, endTime, updatedAt };
  await db
    .insert(s.caseCpr)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseCpr.id, set })
    .run();
}

async function rebuildTourniquet(id: string) {
  const rows = await getEntityEvents(id, "tourniquet");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let limb: string | null = null;
  let applicationTime: number | null = null;
  let removalTime: number | null = null;
  let conversionTime: number | null = null;
  let repositioningTime: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "tourniquet.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        limb = (p.limb as string) ?? limb;
        deleted = false;
        break;
      case "tourniquet.delete":
        deleted = true;
        break;
      case "tourniquet.setLimb":
        limb = p.limb as string;
        break;
      case "tourniquet.setApplicationTime":
        applicationTime = p.timestamp as number;
        break;
      case "tourniquet.setRemovalTime":
        removalTime = p.timestamp as number;
        break;
      case "tourniquet.setConversionTime":
        conversionTime = p.timestamp as number;
        break;
      case "tourniquet.setRepositioningTime":
        repositioningTime = p.timestamp as number;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseTourniquet).where(eq(s.caseTourniquet.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    limb,
    applicationTime,
    removalTime,
    conversionTime,
    repositioningTime,
    updatedAt,
  };
  await db
    .insert(s.caseTourniquet)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseTourniquet.id, set })
    .run();
}

async function rebuildMedication(id: string) {
  const rows = await getEntityEvents(id, "medication");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let time: number | null = null;
  let name: string | null = null;
  let dose: string | null = null;
  let doseUnit: string | null = null;
  let route: string | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "medication.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "medication.delete":
        deleted = true;
        break;
      case "medication.setTime":
        time = p.timestamp as number;
        break;
      case "medication.setName":
        name = p.name as string;
        break;
      case "medication.setDose":
        dose = str(p.dose);
        break;
      case "medication.setDoseUnit":
        doseUnit = p.doseUnit as string;
        break;
      case "medication.setRoute":
        route = p.route as string;
        break;
    }
  }
  if (deleted) {
    await db.delete(s.caseMedication).where(eq(s.caseMedication.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = { caseId, role, time, name, dose, doseUnit, route, updatedAt };
  await db
    .insert(s.caseMedication)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseMedication.id, set })
    .run();
}

async function rebuildSurgery(id: string) {
  const rows = await getEntityEvents(id, "surgery");
  if (!rows.length) return;
  let deleted = false;
  let caseId = "";
  let role: number | null = null;
  let startTime: number | null = null;
  let endTime: number | null = null;
  let surgeryTypes: unknown = null;
  let description: string | null = null;
  let anesthesiaLocal: number | null = null;
  let anesthesiaRegional: number | null = null;
  let anesthesiaGeneral: number | null = null;
  let anesthesiaSedation: number | null = null;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "surgery.create":
        caseId = p.caseUuid as string;
        role = (p.role as number) ?? null;
        deleted = false;
        break;
      case "surgery.delete":
        deleted = true;
        break;
      case "surgery.setStartTime":
        startTime = p.timestamp as number;
        break;
      case "surgery.setEndTime":
        endTime = p.timestamp as number;
        break;
      case "surgery.setSurgeryTypeItem": {
        const a = (surgeryTypes as string[]) || [];
        surgeryTypes = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x) => x !== p.key);
        break;
      }
      case "surgery.setDescription":
        description = p.description as string;
        break;
      case "surgery.setAnesthesiaType": {
        // backward compat: old single-value events
        const v = p.anesthesiaType as string;
        anesthesiaLocal = v === "local" ? 1 : 0;
        anesthesiaRegional = v === "regional" ? 1 : 0;
        anesthesiaGeneral = v === "general" ? 1 : 0;
        anesthesiaSedation = v === "sedation" ? 1 : 0;
        break;
      }
      case "surgery.setAnesthesiaTypeItem": {
        const val = p.value ? 1 : 0;
        switch (p.key as string) {
          case "local": anesthesiaLocal = val; break;
          case "regional": anesthesiaRegional = val; break;
          case "general": anesthesiaGeneral = val; break;
          case "sedation": anesthesiaSedation = val; break;
        }
        break;
      }
    }
  }
  if (deleted) {
    await db.delete(s.caseSurgery).where(eq(s.caseSurgery.id, id)).run();
    return;
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    role,
    startTime,
    endTime,
    surgeryTypes: surgeryTypes as any,
    description,
    anesthesiaLocal,
    anesthesiaRegional,
    anesthesiaGeneral,
    anesthesiaSedation,
    updatedAt,
  };
  await db
    .insert(s.caseSurgery)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.caseSurgery.id, set })
    .run();
}

async function rebuildAttachment(id: string) {
  const rows = await getEntityEvents(id, "attachment");
  if (!rows.length) return;
  let caseId = "";
  let mimeType: string | null = null;
  let capturedAt: number | null = null;
  let sha256: string | null = null;
  let caption: string | null = null;
  let deleted = 0;
  for (const r of rows) {
    const p = r.payload as P;
    switch (r.type) {
      case "attachment.create":
        caseId = p.caseUuid as string;
        mimeType = p.mimeType as string;
        capturedAt = p.capturedAt as number;
        break;
      case "attachment.setData":
        sha256 = p.sha256 as string;
        break;
      case "attachment.setCaption":
        caption = p.caption as string;
        break;
      case "attachment.delete":
        deleted = 1;
        break;
    }
  }
  const updatedAt = maxTs(rows);
  const set = {
    caseId,
    mimeType,
    capturedAt,
    sha256,
    caption,
    deleted,
    updatedAt,
  };
  await db
    .insert(s.attachments)
    .values({ id, ...set })
    .onConflictDoUpdate({ target: s.attachments.id, set })
    .run();
}

// ─── Dispatch table ──────────────────────────────────────────────────

const rebuilders: Record<string, (id: string) => Promise<void>> = {
  region: rebuildRegion,
  hospital: rebuildHospital,
  unit: rebuildUnit,
  user: rebuildUser,
  session: rebuildSession,
  patient: rebuildPatient,
  case: rebuildCase,
  vitals: rebuildVitals,
  ventilation: rebuildVentilation,
  diagnostics: rebuildDiagnostics,
  labValues: rebuildLabValues,
  exam: rebuildExam,
  cpr: rebuildCpr,
  tourniquet: rebuildTourniquet,
  medication: rebuildMedication,
  surgery: rebuildSurgery,
  attachment: rebuildAttachment,
};

// Events that don't affect any materialized view
const noOpTypes = new Set(["user.sawEvents"]);

export async function applyClientProjection(
  event: EventEnvelope,
): Promise<void> {
  if (noOpTypes.has(event.type)) return;

  const prefix = event.type.split(".")[0];
  const entityId = event.entityId;
  const rebuild = rebuilders[prefix];
  if (rebuild) await rebuild(entityId);

  if (event.type === "case.setMedicFacility" || event.type === "case.setR2MedicFacility") {
    const facility = (event.payload as Record<string, unknown>).facility as string | undefined;
    if (facility) {
      const sq = getSqlocal();
      await sq.sql`INSERT INTO medic_unit_usage (user_id, unit_id, last_used_at)
        VALUES (${event.userId}, ${facility}, ${event.clientTimestamp})
        ON CONFLICT(user_id, unit_id) DO UPDATE SET last_used_at = MAX(last_used_at, excluded.last_used_at)`;
    }
  }
}
